import React, { useState } from "react";
import { Card } from "./ui/card.jsx";
import { Button } from "./ui/button.jsx";
import { Input } from "./ui/input.jsx";
import { Textarea } from "./ui/textarea.jsx";
import { useToast } from "../hooks/use-toast.ts";
import { AnimatedSection } from "./IntersectionObserver.tsx";
import { Send, Mail, Phone } from "lucide-react";

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Сообщение отправлено успешно!",
          description: data.message || "Мы ответим в течение 1 рабочего дня.",
          duration: 5000,
        });
        setFormData({ name: "", email: "", company: "", message: "" });
      } else {
        throw new Error(data.detail || "Ошибка отправки");
      }
    } catch (error) {
      toast({
        title: "Ошибка отправки сообщения",
        description: "Попробуйте снова или свяжитесь с нами напрямую.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section pad-xlarge">
      <div className="dark-container">
        <div className="contact-content">
          <AnimatedSection>
            <div className="contact-info">
              <h2 className="display-medium">Расскажите о вашем бренде</h2>
              <p className="body-large contact-subtitle">
                Мы ответим в течение 1 рабочего дня.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <Mail size={24} />
                  <div>
                    <h4 className="heading-3">Email</h4>
                    <p className="body-medium">edgekeycommerce@gmail.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <Phone size={24} />
                  <div>
                    <h4 className="heading-3">Телефон</h4>
                    <p className="body-medium">+375 (29) 820-71-89</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={1}>
            <Card className="contact-form-card">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-field">
                    <Input
                      type="text"
                      name="name"
                      placeholder="Ваше имя"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="contact-input"
                    />
                  </div>
                  <div className="form-field">
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email адрес"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="contact-input"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <Input
                    type="text"
                    name="company"
                    placeholder="Название компании"
                    value={formData.company}
                    onChange={handleChange}
                    className="contact-input"
                  />
                </div>

                <div className="form-field">
                  <Textarea
                    name="message"
                    placeholder="Расскажите о вашем проекте..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="contact-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  className="btn-primary form-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Отправляем..." : "Отправить сообщение"}
                  <Send size={16} />
                </Button>
              </form>
            </Card>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default Contact;